import http, { setAuthToken } from './http'
import apiFunctions from '../helpers/apiFunctions'

const TOKEN_KEY = 'iris_token'
const USER_KEY = 'iris_user'
const STORAGE_KEY = 'iris_storage'
const DEV_AUTH_TOKEN = 'JWT dev-bypass'

const isDevAuthBypassEnabled = () => {
  if (process.env.VUE_APP_DISABLE_AUTH) {
    return process.env.VUE_APP_DISABLE_AUTH === 'true'
  }
  return process.env.NODE_ENV === 'development'
}

const createDevUser = () => apiFunctions.createPermissions({
  id: 1,
  uuid: '00000000-0000-0000-0000-000000000001',
  slug: 'dev-admin-1',
  status: 'enabled',
  email: 'dev.admin@example.local',
  firstname: 'Dev',
  lastname: 'Admin',
  screenname: 'Dev Admin',
  deleted: false,
  password_reset_flag: false,
  role_id: 1,
  role: {
    id: 1,
    uuid: 'dev-admin-role',
    slug: 'administrator',
    name: 'administrator'
  },
  clients: []
})

const getStorage = () => {
  const type = localStorage.getItem(STORAGE_KEY)
  return type === 'session' ? sessionStorage : localStorage
}

const auth = {
  _ready: false,
  _user: null,
  _token: null,
  _refreshTimer: null,

  async init () {
    if (isDevAuthBypassEnabled()) {
      this._user = createDevUser()
      this._token = DEV_AUTH_TOKEN
      this._stopRefresh()
      setAuthToken(DEV_AUTH_TOKEN)
      this._ready = true
      return
    }

    const storage = getStorage()
    const token = storage.getItem(TOKEN_KEY)
    const cachedUser = storage.getItem(USER_KEY)
    if (cachedUser) {
      try {
        this._user = JSON.parse(cachedUser)
      } catch {
        this._user = null
      }
    }
    if (token) {
      this._token = token
      setAuthToken(token)
      try {
        const response = await http.get('/auth/user')
        this._user = apiFunctions.createPermissions(response.data)
        this._startRefresh()
      } catch {
        this._token = null
        setAuthToken(null)
        storage.removeItem(TOKEN_KEY)
        storage.removeItem(USER_KEY)
      }
    } else {
      this._user = null
      this._token = null
    }
    this._ready = true
  },

  ready () {
    return this._ready
  },

  check () {
    return Boolean(this._token)
  },

  user (value) {
    if (typeof value !== 'undefined') {
      const storage = getStorage()
      this._user = value
      storage.setItem(USER_KEY, JSON.stringify(value))
    }
    return this._user
  },

  async login (options = {}) {
    const url = options.url || '/auth/login'
    const data = options.data || options.body || {}
    const response = await http.post(url, data)
    const authHeader = response.headers.authorization || response.headers.Authorization

    if (authHeader) {
      const storageType = options.staySignedIn === false ? 'session' : 'local'
      localStorage.setItem(STORAGE_KEY, storageType)
      const storage = storageType === 'session' ? sessionStorage : localStorage
      storage.setItem(TOKEN_KEY, authHeader)
      this._token = authHeader
      setAuthToken(authHeader)
      this._startRefresh()
    }

    if (options.fetchUser) {
      try {
        const userResponse = await http.get('/auth/user')
        this._user = apiFunctions.createPermissions(userResponse.data)
      } catch {
        this._user = null
      }
    } else if (response.data) {
      this._user = apiFunctions.createPermissions(response.data)
    }

    return response
  },

  async logout ({ redirect } = {}) {
    if (isDevAuthBypassEnabled()) {
      this._user = createDevUser()
      this._token = DEV_AUTH_TOKEN
      this._stopRefresh()
      setAuthToken(DEV_AUTH_TOKEN)
      if (redirect) {
        redirect()
      }
      return
    }

    const storage = getStorage()
    storage.removeItem(TOKEN_KEY)
    storage.removeItem(USER_KEY)
    localStorage.removeItem(STORAGE_KEY)
    this._user = null
    this._token = null
    this._stopRefresh()
    setAuthToken(null)
    if (redirect) {
      redirect()
    }
  },

  _startRefresh () {
    this._stopRefresh()
    this._refreshTimer = setInterval(async () => {
      if (!this._token) {
        return
      }
      try {
        const response = await http.get('/auth/refresh')
        const authHeader = response.headers.authorization || response.headers.Authorization
        if (authHeader) {
          const storage = getStorage()
          storage.setItem(TOKEN_KEY, authHeader)
          this._token = authHeader
          setAuthToken(authHeader)
        }
        if (response.data) {
          this._user = apiFunctions.createPermissions(response.data)
        }
      } catch {
        // ignore refresh failures; user will re-authenticate on 401
      }
    }, 60 * 1000)
  },

  _stopRefresh () {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer)
      this._refreshTimer = null
    }
  }
}

export const authPlugin = {
  install (app) {
    app.config.globalProperties.$auth = auth
  }
}

export default auth
